import { Box, keyframes, type SxProps, type Theme } from "@mui/material";

import { Button, type ButtonProps, type ButtonVariant } from "./Button";

export interface LoadingButtonProps extends ButtonProps {
  isLoading: boolean;
}

/**
 * Determines if button variant should use light (white) spinner.
 * Dark buttons (primary, secondary, inverted variants) get light spinner.
 * Light buttons (outlined, muted, link, danger) get dark spinner.
 */
function shouldUseLightSpinner(variant: ButtonVariant = "primary"): boolean {
  const lightSpinnerVariants: ButtonVariant[] = [
    "primary",
    "secondary",
    "contained",
    "linkInverted",
    "outlineInverted",
  ];

  return lightSpinnerVariants.includes(variant);
}

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

/**
 * A LoadingButton component for the Berlin theme that shows a spinner when loading.
 *
 * This is a local implementation that fixes the spinner color visibility issue.
 * The original @src/lib/ui-react LoadingButton uses theme.palette.action.disabled
 * for the spinner color, which doesn't provide good contrast on Berlin theme buttons.
 *
 * This implementation adapts the spinner color based on the button variant:
 * - Dark buttons (primary, secondary, inverted) use white spinner
 * - Light buttons (outlined, muted, link) use dark spinner
 * - Danger button uses red spinner
 */
export function LoadingButton(props: LoadingButtonProps) {
  const { isLoading, disabled, children, sx, variant, ...rest } = props;

  const useLightSpinner = shouldUseLightSpinner(variant);

  const sxArray: Array<SxProps<Theme>> = (() => {
    if (Array.isArray(sx)) {
      return sx as Array<SxProps<Theme>>;
    }
    if (sx) {
      return [sx];
    }
    return [];
  })();

  const isDisabled = disabled ?? isLoading;

  return (
    <Button
      {...rest}
      variant={variant}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      sx={[{ position: "relative" }, ...sxArray] as SxProps<Theme>}
    >
      {isLoading && (
        <Box
          sx={(theme: Theme) => {
            const lightColor =
              (theme.palette as { base?: { inverted?: { base?: string } } }).base?.inverted?.base ??
              "#FFFFFFE6";
            const darkColor = theme.palette.text?.primary ?? "#000000DE";
            const dangerColor =
              (theme.palette as { base?: { danger?: string } }).base?.danger ?? "#DD1717";

            let spinnerColor: string;
            if (variant === "danger") {
              spinnerColor = dangerColor;
            } else if (useLightSpinner) {
              spinnerColor = lightColor;
            } else {
              spinnerColor = darkColor;
            }

            return {
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "24px",
              height: "24px",
              color: spinnerColor,
            };
          }}
        >
          <Box
            component="svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            sx={{
              display: "block",
              animation: `${spin} 1s linear infinite`,
            }}
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="60"
              strokeDashoffset="15"
              opacity="0.9"
            />
          </Box>
        </Box>
      )}
      <Box component="span" sx={{ opacity: isLoading ? 0 : 1 }}>
        {children}
      </Box>
    </Button>
  );
}
